import React, { useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import ChatPage from './pages/ChatPage';
import CheckInPage from './pages/CheckInPage';
import ReportsPage from './pages/ReportsPage';
import ReportDetailPage from './pages/ReportDetailPage';
import ProfilePage from './pages/ProfilePage';
import GenotypePage from './pages/GenotypePage';
import RemindersPage from './pages/RemindersPage';
import DietPage from './pages/DietPage';
import SupplementsPage from './pages/SupplementsPage';
import LifestylePage from './pages/LifestylePage';

// Static Pages
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PillarPage from './pages/PillarPage';
import ServicesPage from './pages/ServicesPage';
import ServiceDetailPage from './pages/ServiceDetailPage';

// Admin Pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminUserDetailPage from './pages/admin/AdminUserDetailPage';
import AdminDocumentsPage from './pages/admin/AdminDocumentsPage';
import AdminHealthGoalsPage from './pages/admin/AdminHealthGoalsPage';
import AdminGenotypesPage from './pages/admin/AdminGenotypesPage';
import AdminHealthIssuesPage from './pages/admin/AdminHealthIssuesPage';
import AdminDocumentFoldersPage from './pages/admin/AdminDocumentFoldersPage';
import AdminAccessRequestsPage from './pages/admin/AdminAccessRequestsPage';
import AdminTestimonialsPage from './pages/admin/AdminTestimonialsPage';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Protected Route Component
function ProtectedRoute({ children, requireOnboarding = true, adminOnly = false }) {
  const { isAuthenticated, loading, user, needsOnboarding, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireOnboarding && needsOnboarding && !isAdmin) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
}

// Public Route (redirect if authenticated)
function PublicRoute({ children }) {
  const { isAuthenticated, loading, user, needsOnboarding, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (isAuthenticated) {
    if (isAdmin) {
      return <Navigate to="/admin" replace />;
    }
    if (needsOnboarding) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      
      {/* Static Pages */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/pillars/:slug" element={<PillarPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/services/:slug" element={<ServiceDetailPage />} />

      {/* Onboarding */}
      <Route path="/onboarding" element={
        <ProtectedRoute requireOnboarding={false}>
          <OnboardingPage />
        </ProtectedRoute>
      } />

      {/* User Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/chat" element={
        <ProtectedRoute>
          <ChatPage />
        </ProtectedRoute>
      } />
      <Route path="/checkin" element={
        <ProtectedRoute>
          <CheckInPage />
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute>
          <ReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/reports/:reportId" element={
        <ProtectedRoute>
          <ReportDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/genotype/:genotypeId" element={
        <ProtectedRoute>
          <GenotypePage />
        </ProtectedRoute>
      } />
      <Route path="/reminders" element={
        <ProtectedRoute>
          <RemindersPage />
        </ProtectedRoute>
      } />
      <Route path="/diet" element={
        <ProtectedRoute>
          <DietPage />
        </ProtectedRoute>
      } />
      <Route path="/supplements" element={
        <ProtectedRoute>
          <SupplementsPage />
        </ProtectedRoute>
      } />
      <Route path="/lifestyle" element={
        <ProtectedRoute>
          <LifestylePage />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <AdminDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute adminOnly>
          <AdminUsersPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/users/:userId" element={
        <ProtectedRoute adminOnly>
          <AdminUserDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/documents" element={
        <ProtectedRoute adminOnly>
          <AdminDocumentsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/health-goals" element={
        <ProtectedRoute adminOnly>
          <AdminHealthGoalsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/genotypes" element={
        <ProtectedRoute adminOnly>
          <AdminGenotypesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/health-issues" element={
        <ProtectedRoute adminOnly>
          <AdminHealthIssuesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/document-folders" element={
        <ProtectedRoute adminOnly>
          <AdminDocumentFoldersPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/access-requests" element={
        <ProtectedRoute adminOnly>
          <AdminAccessRequestsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/testimonials" element={
        <ProtectedRoute adminOnly>
          <AdminTestimonialsPage />
        </ProtectedRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          toastOptions={{
            style: {
              background: 'white',
              border: '1px solid rgba(232, 160, 149, 0.2)',
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
